import React, { useState } from 'react';
import { ChangeLog } from '../../types/mold';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ArrowRight,
} from 'lucide-react';

export interface EventLogProps {
  events: ChangeLog[];
  /** Total matching logs (independent of pagination). */
  total?: number;
  /** Current zero-based page index. */
  page?: number;
  /** Page size used together with `total` to render the pagination footer. */
  pageSize?: number;
  /** Called with the new page index when prev/next is clicked. */
  onPageChange?: (page: number) => void;
  loading?: boolean;
  error?: string | null;
}

// Thai display labels for every canonical change_logs entity_type.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  mold: 'แม่พิมพ์',
  mold_revision: 'ฉบับแม่พิมพ์',
  mold_order: 'ใบงาน',
  mold_order_state_instance: 'สถานะใบงาน',
  state_instance_approver: 'ผู้อนุมัติสถานะ',
  mold_product_part: 'ชิ้นส่วนในแม่พิมพ์',
  customer: 'ลูกค้า',
  product_part: 'ชิ้นส่วน',
  file_metadata: 'ไฟล์แนบ',
  role: 'บทบาทผู้ใช้',
  insert: 'แม่พิมพ์ย่อย (Insert)',
  mold_insert: 'Insert ในแม่พิมพ์',
  injection_machine: 'เครื่องจักร',
  selection_config: 'การตั้งค่าตัวเลือก',
};

// Thai labels for frequently seen change_details field names. Unlisted fields
// fall back to the raw column name — auditors still see exactly what changed.
const FIELD_LABELS: Record<string, string> = {
  status: 'สถานะ',
  assigned_to: 'ผู้รับผิดชอบ (id)',
  remarks: 'หมายเหตุ',
  description: 'รายละเอียด',
  customer_id: 'ลูกค้า (id)',
  mold_id: 'แม่พิมพ์ (id)',
  mold_code: 'รหัสแม่พิมพ์',
  mold_name: 'ชื่อแม่พิมพ์',
  current_location: 'ที่ตั้งปัจจุบัน',
  storage_location: 'ที่จัดเก็บ',
  order_detail: 'ข้อมูลฟอร์มใบงาน',
  extra_attributes: 'ข้อมูลเพิ่มเติม',
  planned_working_date: 'วันที่วางแผน',
  completed_at: 'เสร็จเมื่อ',
  cycle_shot_count: 'จำนวนชอตรอบล้าง',
  total_shot_count: 'จำนวนชอตสะสม',
  updated_at: 'เวลาแก้ไขล่าสุด',
  created_at: 'สร้างเมื่อ',
};

const translateEntityType = (entityType: string): string =>
  ENTITY_TYPE_LABELS[entityType] ?? entityType;

const fieldLabel = (key: string): string => FIELD_LABELS[key] ?? key;

const translateUsername = (username: string): string => {
  switch (username) {
    case 'admin':
      return 'ผู้ดูแลระบบ';
    case 'system':
      return 'ระบบ';
    default:
      return username;
  }
};

// Prefer the human name over the login/employee-number username.
const actorName = (changedBy: ChangeLog['changedBy']): string => {
  const fullname = (changedBy as { fullname?: string }).fullname;
  return fullname && fullname.trim() !== ''
    ? fullname
    : translateUsername(changedBy.username);
};

// Bookkeeping columns that repeat on every write and carry no user-facing
// meaning. Hidden from summaries; shown dimmed at the end of the detail list.
// Kept in sync with the backend bookkeepingKeys set (pkg/diff/diff.go), which
// now drops update rows that reduce to only these — so a bare updated_at bump
// should rarely reach the UI, but the fallback below stays defensive.
const NOISE_KEYS = new Set(['updated_at', 'created_at', 'id']);

// Defense in depth: never render credential-shaped fields even if a legacy
// row slipped past the backend redaction (pkg/diff.RedactSensitiveKeys and
// migration 0043 strip them at the source).
const SENSITIVE_KEY = /password|secret|token/i;

// A change_details field value is either a diff record ({old, new}) written by
// the `update` path, or a flat snapshot value written by the `create`/`delete`
// paths (see docs/backend/audit_logs.md, "The three write patterns").
const isChangeRecord = (
  value: unknown,
): value is { old?: unknown; new?: unknown } =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  ('old' in value || 'new' in value);

// Normalize any change_details value into an {old, new} pair for rendering:
// diff records pass through; flat snapshot values land in `new` for creates
// (the value after the change) and in `old` for deletes (the value before).
const toChangeRecord = (
  value: unknown,
  changeType: string,
): { old?: unknown; new?: unknown } => {
  if (isChangeRecord(value)) return value;
  return changeType === 'delete' ? { old: value } : { new: value };
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

// change_details values are JSON-shaped (string|number|bool|null|array|object).
// Render them readably; fall back to '—' for emptiness.
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่ใช่';
  if (typeof value === 'number') return String(value);
  let text: string;
  if (typeof value === 'string') {
    if (ISO_DATE.test(value)) {
      try {
        return format(new Date(value), 'd MMM yyyy, HH:mm', { locale: th });
      } catch {
        return value;
      }
    }
    text = value;
  } else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }
  // Opaque blobs (base64 form payloads etc.) are meaningless inline.
  return text.length > 80 ? `${text.slice(0, 77)}…` : text;
};

interface DetailField {
  key: string;
  label: string;
  record: { old?: unknown; new?: unknown };
  sensitive: boolean;
  noise: boolean;
}

const toDetailFields = (event: ChangeLog): DetailField[] => {
  const fields = Object.entries(event.changeDetails).map(([key, value]) => {
    const sensitive = SENSITIVE_KEY.test(key);
    return {
      key,
      label: fieldLabel(key),
      record: sensitive
        ? { old: '•••', new: '•••' }
        : toChangeRecord(value, event.changeType),
      sensitive,
      noise: NOISE_KEYS.has(key),
    };
  });
  // Meaningful fields first, bookkeeping noise last.
  fields.sort((a, b) => Number(a.noise) - Number(b.noise));
  return fields;
};

const CHANGE_TYPE_META: Record<
  string,
  {
    icon: React.ReactNode;
    label: string;
    pillClass: string;
    borderClass: string;
  }
> = {
  create: {
    icon: <Plus size={13} />,
    label: 'สร้าง',
    pillClass: 'bg-green-100 text-green-700 ring-green-600/20',
    borderClass: 'border-green-400',
  },
  update: {
    icon: <Pencil size={13} />,
    label: 'แก้ไข',
    pillClass: 'bg-blue-100 text-blue-700 ring-blue-600/20',
    borderClass: 'border-blue-400',
  },
  delete: {
    icon: <Trash2 size={13} />,
    label: 'ลบ',
    pillClass: 'bg-red-100 text-red-700 ring-red-600/20',
    borderClass: 'border-red-400',
  },
};

const changeTypeMeta = (changeType: string) =>
  CHANGE_TYPE_META[changeType] ?? CHANGE_TYPE_META.update;

// Colored pill making the kind of change scannable at a glance.
const TypePill: React.FC<{ changeType: string }> = ({ changeType }) => {
  const meta = changeTypeMeta(changeType);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.pillClass}`}
    >
      {meta.icon}
      {meta.label}
    </span>
  );
};

const FieldValue: React.FC<{
  record: { old?: unknown; new?: unknown };
  changeType: string;
}> = ({ record, changeType }) => {
  if (changeType === 'update') {
    return (
      <span className='inline-flex flex-wrap items-center gap-1'>
        <span className='text-gray-500 line-through decoration-gray-300'>
          {formatValue(record.old)}
        </span>
        <ArrowRight size={12} className='shrink-0 text-gray-400' />
        <span>{formatValue(record.new)}</span>
      </span>
    );
  }
  // create shows the initial value; delete shows the value that was removed
  return (
    <span>
      {formatValue(changeType === 'delete' ? record.old : record.new)}
    </span>
  );
};

const EventRow: React.FC<{ event: ChangeLog }> = ({ event }) => {
  const [expanded, setExpanded] = useState(false);

  const meta = changeTypeMeta(event.changeType);
  const fields = toDetailFields(event);
  // For creates, empty initial values are pure noise — drop them entirely.
  const visibleFields =
    event.changeType === 'create'
      ? fields.filter(
          (f) => f.noise || f.sensitive || formatValue(f.record.new) !== '—',
        )
      : fields;
  const meaningful = visibleFields.filter((f) => !f.noise);
  // A log whose only content is bookkeeping (e.g. a bare updated_at bump that
  // predates the backend noise filter) still has to render — fall back to the
  // noise fields.
  const summaryFields = meaningful.length > 0 ? meaningful : visibleFields;
  const preview = summaryFields[0];
  const canExpand = visibleFields.length > 0;

  const summaryText =
    event.changeType === 'update'
      ? `เปลี่ยน ${summaryFields.length} รายการ`
      : event.changeType === 'delete'
        ? `ลบข้อมูล ${summaryFields.length} ฟิลด์`
        : `ข้อมูลเริ่มต้น ${summaryFields.length} ฟิลด์`;

  const toggle = () => canExpand && setExpanded((v) => !v);

  return (
    <>
      <tr
        className={`border-l-4 ${meta.borderClass} ${
          canExpand ? 'cursor-pointer hover:bg-gray-50' : ''
        }`}
        onClick={canExpand ? toggle : undefined}
      >
        <td className='px-3 py-2.5 align-top'>
          <TypePill changeType={event.changeType} />
        </td>
        <td className='px-3 py-2.5 align-top'>
          <div className='font-medium text-gray-900'>
            {translateEntityType(event.entityType)}
          </div>
          <div className='mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-gray-600'>
            <span>{summaryText}</span>
            {preview && (
              <span className='inline-flex min-w-0 flex-wrap items-center gap-1 text-gray-500'>
                <span>{preview.label}:</span>
                <FieldValue
                  record={preview.record}
                  changeType={event.changeType}
                />
              </span>
            )}
          </div>
        </td>
        <td className='px-3 py-2.5 align-top whitespace-nowrap text-sm text-gray-700'>
          {actorName(event.changedBy)}
        </td>
        <td className='px-3 py-2.5 align-top whitespace-nowrap text-xs text-gray-400'>
          {format(new Date(event.changeTime), 'd MMM yyyy, HH:mm', {
            locale: th,
          })}
        </td>
        <td className='px-2 py-2.5 align-top text-right'>
          {canExpand && (
            <button
              type='button'
              aria-label={expanded ? 'ซ่อนรายละเอียด' : 'รายละเอียด'}
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              className='text-gray-400 hover:text-gray-600'
            >
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className={`border-l-4 ${meta.borderClass} bg-gray-50/60`}>
          <td className='hidden sm:table-cell' />
          <td colSpan={4} className='px-3 pb-3 pt-1'>
            <dl className='space-y-1 border-l-2 border-gray-200 pl-3'>
              {visibleFields.map((f) => (
                <div
                  key={f.key}
                  className={`flex flex-wrap gap-x-2 text-sm ${
                    f.noise ? 'text-gray-400' : 'text-gray-700'
                  }`}
                >
                  <dt className='font-medium'>{f.label}:</dt>
                  <dd className='min-w-0 break-all'>
                    <FieldValue
                      record={f.record}
                      changeType={event.changeType}
                    />
                  </dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
};

const EventLog: React.FC<EventLogProps> = ({
  events,
  total,
  page = 0,
  pageSize = 10,
  onPageChange,
  loading = false,
  error = null,
}) => {
  const renderPagination = () => {
    if (total === undefined || !onPageChange) return null;
    const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 1;
    if (totalPages <= 1) {
      return (
        <div className='mt-4 text-sm text-gray-500'>ทั้งหมด {total} รายการ</div>
      );
    }
    return (
      <div className='mt-4 flex items-center justify-between text-sm text-gray-600'>
        <span>
          หน้า {page + 1}/{totalPages} · ทั้งหมด {total} รายการ
        </span>
        <div className='flex gap-2'>
          <button
            type='button'
            aria-label='หน้าแรก'
            disabled={page === 0}
            onClick={() => onPageChange(0)}
            className='px-2 py-1 border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50'
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            type='button'
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            className='px-3 py-1 border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50'
          >
            ก่อนหน้า
          </button>
          <button
            type='button'
            disabled={page + 1 >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className='px-3 py-1 border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50'
          >
            ถัดไป
          </button>
          <button
            type='button'
            aria-label='หน้าสุดท้าย'
            disabled={page + 1 >= totalPages}
            onClick={() => onPageChange(totalPages - 1)}
            className='px-2 py-1 border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50'
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className='text-center py-4 text-gray-500'>กำลังโหลด...</div>;
  }

  if (error) {
    return (
      <div className='bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md'>
        {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200 text-center'>
        <p className='text-base text-gray-600'>ยังไม่มีประวัติการเปลี่ยนแปลง</p>
      </div>
    );
  }

  return (
    <div className='py-4'>
      <div className='overflow-x-auto rounded-lg border border-gray-200'>
        <table className='min-w-full text-left'>
          <thead className='bg-gray-50 text-xs font-medium text-gray-500'>
            <tr>
              <th scope='col' className='px-3 py-2 font-medium'>
                ประเภท
              </th>
              <th scope='col' className='px-3 py-2 font-medium'>
                รายการ
              </th>
              <th scope='col' className='px-3 py-2 font-medium'>
                ผู้แก้ไข
              </th>
              <th scope='col' className='px-3 py-2 font-medium'>
                เวลา
              </th>
              <th scope='col' className='px-2 py-2'>
                <span className='sr-only'>รายละเอียด</span>
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {events.map((event, index) => (
              <EventRow key={event.id ?? index} event={event} />
            ))}
          </tbody>
        </table>
      </div>
      {renderPagination()}
    </div>
  );
};

export default EventLog;
